import React, { FC, useEffect, useRef, useState } from 'react';

interface VideoCarouselProps {
  videoSources: string[];
}

const VideoCarousel: FC<VideoCarouselProps> = ({ videoSources }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [autoplayFailed, setAutoplayFailed] = useState(false);

  const handleEnded = (): void => {
    if (videoSources.length > 1) {
      setCurrentIndex((prev) => (prev + 1) % videoSources.length);
    }
  };

  useEffect(() => {
    Object.defineProperty(HTMLMediaElement.prototype, 'playing', {
      get() {
        return (
          this.currentTime > 0 &&
          !this.paused &&
          !this.ended &&
          this.readyState > 2
        );
      },
      configurable: true,
    });
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = async () => {
      try {
        await video.play();
        setAutoplayFailed(false);
      } catch (err) {
        console.warn('Autoplay failed on visibility:', err);
        setAutoplayFailed(true);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          tryPlay();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, [currentIndex]);

  useEffect(() => {
    if (!autoplayFailed) return;

    const handleInteraction = () => {
      const video = videoRef.current;
      if (video && !(video as any).playing) {
        video.play().then(() => {
          setAutoplayFailed(false);
          window.removeEventListener('click', handleInteraction);
          window.removeEventListener('touchstart', handleInteraction);
        });
      }
    };

    window.addEventListener('click', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);

    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [autoplayFailed]);

  if (!videoSources || videoSources.length === 0) {
    return <div className="text-white">No video available.</div>;
  }

  return (
    <div className="w-full h-full min-h-screen bg-black">
      <video
        id="home_video"
        ref={videoRef}
        key={videoSources[currentIndex]}
        src={videoSources[currentIndex]}
        autoPlay
        muted
        playsInline
        preload="auto"
        controls={false}
        loop={videoSources.length === 1}
        onEnded={handleEnded}
        className="w-full h-full object-cover"
        onError={(e) => console.error('Video error:', e)}
      >
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

export default VideoCarousel;
